import { useState, type FormEvent } from "react";
import { CircleX, ExternalLink, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAdoPatStatus, useConnectAdoPat } from "@/hooks/queries/use-me";
import { canAnywhere, useToolboxAccess } from "@/hooks/queries/use-toolbox-access";
import type { AdoPatHowTo, AdoPatStatus } from "@/lib/api/me";
import { cn } from "@/lib/utils";
import { TOOLBOX_TEXT } from "@/lib/toolbox/type-scale";
import { StepRow } from "./setup-step-list";

/** The reminder shows this many days before the token stops working (frame 1c: "reminds you a week before"). */
export const ADO_PAT_REMINDER_DAYS = 7;

const DAY_MS = 24 * 60 * 60 * 1000;

/** Row 2's line while the step is not done (frame 1c). */
export const CONNECT_ADO_INTRO =
  "The Toolbox reads your backlog and posts briefs with a personal access token (PAT).";

/** Shown when SpecTree itself could not test the token (no answer, server error). */
const CONNECT_ERROR = "SpecTree could not test the token right now. Try again in a moment.";

/** Row 2's line when the saved token is past its expiry (savedAt + expiryDays). */
export const ADO_PAT_EXPIRED = "Your token has expired. Make a new one.";

/** Row 2's line for a token saved without the connect test (Settings, or before tests were recorded). */
export const ADO_PAT_UNTESTED = "Token saved";

/** The warning when a repository setter's token reads work items but not code (`codeRead: false`). */
export const ADO_PAT_NO_CODE_READ =
  "This token can read work items but not code. To set up repositories, make one with Code: Read too.";

/**
 * The saved token's state, from GET /me/ado-pat (`savedAt` is set only by a
 * tested save):
 * - `none`: nothing saved;
 * - `untested`: saved without the connect test, or before tests were recorded
 *   (`savedAt` null), so no date is known;
 * - `expired`: `savedAt + expiryDays` has passed;
 * - `working`: tested and not past that date.
 * Pure; exported for tests.
 */
export type AdoPatState = "none" | "untested" | "expired" | "working";

function savedAtMs(status: Pick<AdoPatStatus, "savedAt">): number | null {
  if (!status.savedAt) return null;
  const at = Date.parse(status.savedAt);
  return Number.isNaN(at) ? null : at;
}

export function adoPatState(status: Pick<AdoPatStatus, "saved" | "savedAt" | "howTo"> | undefined, now: Date): AdoPatState {
  if (!status?.saved) return "none";
  const at = savedAtMs(status);
  if (at === null) return "untested";
  return now.getTime() >= at + status.howTo.expiryDays * DAY_MS ? "expired" : "working";
}

/** True when step 2 passes: a token is saved and not past its expiry. Pure; exported for tests. */
export function adoPatStepOk(status: Pick<AdoPatStatus, "saved" | "savedAt" | "howTo"> | undefined, now: Date): boolean {
  const state = adoPatState(status, now);
  return state === "working" || state === "untested";
}

/**
 * When a saved PAT stops working, if it is close enough to remind the person:
 * `savedAt + expiryDays`, once `savedAt + expiryDays - 7` days has passed and
 * while that date is still ahead (a past date is "expired", never a reminder).
 * Pure; exported for tests.
 */
export function adoPatExpiry(status: Pick<AdoPatStatus, "saved" | "savedAt" | "howTo">, now: Date): Date | null {
  if (adoPatState(status, now) !== "working") return null;
  const savedAt = savedAtMs(status) ?? 0;
  const remindFrom = savedAt + (status.howTo.expiryDays - ADO_PAT_REMINDER_DAYS) * DAY_MS;
  if (now.getTime() < remindFrom) return null;
  return new Date(savedAt + status.howTo.expiryDays * DAY_MS);
}

/**
 * Who sees the Admins' extra scope line: `holder` (holds `manage-repos`
 * somewhere, so the repository pickers need Code: Read), `none` (does not), or
 * `unknown` while access is not known, when the line is shown addressed to
 * Admins. Pure; exported for tests.
 */
export type AdminScopeAudience = "holder" | "none" | "unknown";

export function adminScopeAudience(access: Parameters<typeof canAnywhere>[0]): AdminScopeAudience {
  if (!access) return "unknown";
  return canAnywhere(access, "manage-repos") ? "holder" : "none";
}

/** "dev.azure.com/rivcode → User settings → Personal access tokens → New Token", from the tokens URL. */
function tokensPath(howTo: AdoPatHowTo): string {
  const site = howTo.tokensUrl.replace(/^https?:\/\//, "").replace(/\/_usersSettings\/tokens\/?$/, "");
  return `${site} → User settings → Personal access tokens → New Token`;
}

function HowNumber({ n }: { n: number }) {
  return (
    <span
      className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full border border-[#3F3F46] text-[11px] font-semibold text-muted-foreground"
      aria-hidden="true"
    >
      {n}
    </span>
  );
}

/** What a passed test tells the page. */
export interface AdoPatPassed {
  /** Only for people who set up repositories: false when the token cannot read code. */
  codeRead?: boolean;
}

export interface ConnectAdoStepProps {
  /** Open the panel even when a PAT is saved (the redirect asked for this step). */
  initiallyOpen?: boolean;
  /** Called after a token passes ADO's test and is saved. */
  onPassed?: (result: AdoPatPassed) => void;
}

/**
 * Setup step 2, "Connect Azure DevOps" (frame 1c; 03-evaluation.md section
 * 8.6 Q5, UX-PM-39): how to make the token with the exact values to fill in,
 * a button that opens ADO's token page, and a field that tests the PAT
 * against ADO on save (POST /me/ado-pat/connect). A refused token shows ADO's
 * reason and stores nothing. With a tested token the row reads "Works" with a
 * ghost "Change", and a week before it stops working it says so. Past that
 * date the step is not done ("Your token has expired. Make a new one.") and
 * the form is open. A token saved untested (Settings, or before tests were
 * recorded) reads "Token saved" with "Test it", which opens the form: there
 * is no date to show. For people who set up repositories a token that
 * cannot read code passes with a warning to make one with Code: Read too.
 *
 * The PAT field is a password field; the token is never shown or kept after
 * it is saved.
 */
export function ConnectAdoStep({ initiallyOpen = false, onPassed }: ConnectAdoStepProps) {
  const statusQuery = useAdoPatStatus();
  const connect = useConnectAdoPat();
  const { data: access } = useToolboxAccess();
  const audience = adminScopeAudience(access);
  const status = statusQuery.data;
  const state = statusQuery.isPending ? null : adoPatState(status, new Date());
  const stepOk = state === "working" || state === "untested";

  const [openChoice, setOpenChoice] = useState<boolean | null>(initiallyOpen ? true : null);
  const [pat, setPat] = useState("");
  const [failure, setFailure] = useState<string | null>(null);
  const [readable, setReadable] = useState<string | null>(null);
  const [noCodeRead, setNoCodeRead] = useState(false);

  const open = openChoice ?? (state !== null && !stepOk);
  const expiry = status ? adoPatExpiry(status, new Date()) : null;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const token = pat.trim();
    if (!token) return;
    setFailure(null);
    connect.mutate(
      { pat: token },
      {
        onSuccess: (result) => {
          if (result.ok) {
            setPat("");
            setReadable(result.readableProjects[0] ?? null);
            setNoCodeRead(result.codeRead === false);
            setOpenChoice(false);
            onPassed?.(result.codeRead !== undefined ? { codeRead: result.codeRead } : {});
          } else {
            setFailure(result.message);
          }
        },
        onError: () => {
          setFailure(CONNECT_ERROR);
        },
      },
    );
  };

  const detail =
    state === null ? (
      "Checking your Azure DevOps connection…"
    ) : readable && stepOk ? (
      <>
        Works: can read <b className="font-semibold text-foreground">{readable}</b>
      </>
    ) : state === "working" ? (
      "Works: token saved and tested"
    ) : state === "untested" ? (
      ADO_PAT_UNTESTED
    ) : state === "expired" ? (
      ADO_PAT_EXPIRED
    ) : (
      CONNECT_ADO_INTRO
    );

  const toggle = () => {
    setOpenChoice(!open);
  };
  const action =
    state === "working" ? (
      <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={toggle}>
        {open ? "Cancel" : "Change"}
      </Button>
    ) : state === "untested" ? (
      <Button variant="outline" size="sm" onClick={toggle}>
        {open ? "Cancel" : "Test it"}
      </Button>
    ) : null;

  const reminder = expiry ? (
    <span className={cn("mt-1.5", TOOLBOX_TEXT.content, "text-[#FDE68A]")} data-testid="connect-ado-reminder">
      Your Azure DevOps token stops working around{" "}
      {expiry.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}.{" "}
      <button type="button" className="underline underline-offset-2" onClick={() => {
          setOpenChoice(true);
        }}>
        Make a new one.
      </button>
    </span>
  ) : null;

  const codeWarning = noCodeRead && stepOk ? (
    <span className={cn("mt-1.5", TOOLBOX_TEXT.content, "text-[#FDE68A]")} role="status" data-testid="connect-ado-code-read">
      {ADO_PAT_NO_CODE_READ}
    </span>
  ) : null;

  const howTo = status?.howTo;
  const panel = open ? (
    <div
      className="flex flex-col gap-3 rounded-[10px] border border-[#27272A] bg-[#111113] px-[18px] py-4"
      data-testid="connect-ado-panel"
    >
      {howTo ? (
        <>
          <div className="grid grid-cols-[22px_minmax(0,1fr)] items-start gap-2.5">
            <HowNumber n={1} />
            <span className="flex flex-wrap items-center gap-2.5">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => window.open(howTo.tokensUrl, "_blank", "noopener,noreferrer")}
              >
                <ExternalLink aria-hidden="true" />
                Open Azure DevOps tokens
              </Button>
              <span className={cn(TOOLBOX_TEXT.meta, TOOLBOX_TEXT.faint)}>{tokensPath(howTo)}</span>
            </span>
          </div>
          <div className="grid grid-cols-[22px_minmax(0,1fr)] items-start gap-2.5">
            <HowNumber n={2} />
            <span className="flex flex-col gap-1.5">
              <span className={TOOLBOX_TEXT.content}>Fill it in exactly like this:</span>
              <dl
                className={cn(
                  "grid grid-cols-[120px_minmax(0,1fr)] gap-x-3 gap-y-1 rounded-lg border border-[#1F1F23] bg-[#0B0B0D] px-3 py-2.5",
                  TOOLBOX_TEXT.content,
                )}
                data-testid="connect-ado-values"
              >
                <dt className={TOOLBOX_TEXT.faint}>Name</dt>
                <dd className="font-semibold text-foreground">{howTo.tokenName}</dd>
                <dt className={TOOLBOX_TEXT.faint}>Organization</dt>
                <dd className="font-semibold text-foreground">{howTo.organization}</dd>
                <dt className={TOOLBOX_TEXT.faint}>Expiration</dt>
                <dd className="font-semibold text-foreground">
                  {howTo.expiryDays} days (the Toolbox reminds you a week before)
                </dd>
                <dt className={TOOLBOX_TEXT.faint}>Scopes</dt>
                <dd className="font-semibold text-foreground">Custom defined → {howTo.scope}</dd>
                {audience === "holder" ? (
                  <>
                    <dt className={TOOLBOX_TEXT.faint}>Also tick</dt>
                    <dd className="font-semibold text-foreground" data-testid="connect-ado-admin-scope">
                      {howTo.adminScope}{" "}
                      <span className={cn("font-normal", TOOLBOX_TEXT.faint)}>
                        (you set up repositories, and picking them needs it)
                      </span>
                    </dd>
                  </>
                ) : audience === "unknown" ? (
                  <>
                    <dt className={TOOLBOX_TEXT.faint}>Admins: also</dt>
                    <dd className="font-semibold text-foreground" data-testid="connect-ado-admin-scope">
                      {howTo.adminScope}
                    </dd>
                  </>
                ) : null}
              </dl>
            </span>
          </div>
        </>
      ) : null}
      <form className="grid grid-cols-[22px_minmax(0,1fr)] items-start gap-2.5" onSubmit={submit}>
        <HowNumber n={howTo ? 3 : 1} />
        <span className="flex flex-col gap-2">
          <label htmlFor="connect-ado-pat" className={TOOLBOX_TEXT.content}>
            Paste the token here:
          </label>
          <span className="flex gap-2">
            <span className="relative flex-1">
              <KeyRound
                className={cn("pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2", TOOLBOX_TEXT.faint)}
                aria-hidden="true"
              />
              <Input
                id="connect-ado-pat"
                type="password"
                autoComplete="off"
                spellCheck={false}
                value={pat}
                onChange={(event) => {
                  setPat(event.target.value);
                }}
                className="border-[#27272A] bg-[#111113] pl-8 font-mono"
              />
            </span>
            <Button
              type="submit"
              size="sm"
              className="bg-primary font-semibold text-primary-foreground hover:bg-primary/90"
              disabled={pat.trim().length === 0 || connect.isPending}
            >
              {connect.isPending ? "Testing…" : "Save and test"}
            </Button>
          </span>
          {failure ? (
            <span
              className={cn("flex items-start gap-1.5 text-[#FF8A9B]", TOOLBOX_TEXT.content)}
              role="alert"
              data-testid="connect-ado-failure"
            >
              <CircleX className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <span>Doesn&apos;t work yet: {failure}</span>
            </span>
          ) : null}
        </span>
      </form>
    </div>
  ) : null;

  return (
    <StepRow
      id="azure-devops"
      status={stepOk ? "ok" : "todo"}
      detail={detail}
      action={action}
      below={
        reminder || codeWarning ? (
          <>
            {reminder}
            {codeWarning}
          </>
        ) : null
      }
      {...(panel ? { expand: panel } : {})}
    />
  );
}
